import React from "react";
import { Link } from "react-router-dom";
import { ArrowRight, ExternalLink } from "lucide-react";
import { Button } from "@/components/ui/button";
import Container from "@/components/marketing/Container";
import SectionHeading from "@/components/marketing/SectionHeading";
import { formatPrice } from "@/lib/utils";

/**
 * Renders the placement options section for a multi-option outlet page.
 * Each option is a distinct selectable offering showing placement type,
 * link type, price, and a purchase action.
 */
export default function OutletOptions({ outlet, options }) {
  return (
    <section className="py-7 sm:py-9 lg:py-12 bg-slate/50">
      <Container>
        <SectionHeading
          tag="Placement Options"
          title={`Choose Your ${outlet.name} Placement`}
          subtitle="Multiple placement options are available for this publication. Each option is a distinct product — select the one that fits your needs."
        />
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6 max-w-5xl mx-auto">
          {options.map((opt, i) => (
            <div key={opt.id} className="bg-white border border-border rounded-xl p-6 flex flex-col hover:border-cobalt/30 transition-colors">
              <div className="flex items-center justify-between mb-4">
                <span className="font-mono text-[10px] uppercase tracking-widest text-cobalt bg-cobalt/10 px-2 py-1 rounded">
                  Option {i + 1}
                </span>
                {opt.link_type && (
                  <span className={`font-mono text-[10px] uppercase tracking-wider px-2 py-1 rounded ${
                    opt.link_type === "dofollow"
                      ? "bg-emerald-50 text-emerald-700"
                      : "bg-gray-100 text-gray-600"
                  }`}>
                    {opt.link_type === "dofollow" ? "DoFollow" : "NoFollow"}
                  </span>
                )}
              </div>
              <h3 className="font-heading text-lg font-bold text-bone mb-2">{opt.placement_type}</h3>
              {opt.price != null ? (
                <div className="font-heading text-3xl font-bold text-cobalt mb-4">${formatPrice(opt.price)}</div>
              ) : (
                <div className="text-sm text-steel mb-4">Contact for pricing</div>
              )}
              {opt.sample_live_url && (
                <a href={opt.sample_live_url} target="_blank" rel="noopener noreferrer" className="text-xs text-cobalt hover:underline mb-4 inline-flex items-center gap-1">
                  View sample placement <ExternalLink className="w-3 h-3" />
                </a>
              )}
              <div className="mt-auto pt-4">
                <Link to={`/dashboard/new-order?package=${opt.id}`}>
                  <Button className="w-full bg-cobalt hover:bg-cobalt/90 text-white">
                    Select &amp; Order <ArrowRight className="ml-2 w-4 h-4" />
                  </Button>
                </Link>
              </div>
            </div>
          ))}
        </div>
      </Container>
    </section>
  );
}